import React, { useRef, useEffect, useState } from 'react';
import { Typography, Container, Box, useMediaQuery } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import CodeIcon from '@mui/icons-material/Code';
import DevicesIcon from '@mui/icons-material/Devices';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import './WhyChooseSection.css';

const features = [
  {
    icon: <CodeIcon sx={{ fontSize: 40 }} />,
    title: "Full-Stack Excellence",
    description: "Our expert developers deliver end-to-end solutions using the latest technologies, from robust backend systems to engaging frontend experiences."
  },
  {
    icon: <DevicesIcon sx={{ fontSize: 40 }} />,
    title: "Cross-Platform Solutions",
    description: "We build responsive applications that work seamlessly across web, mobile, and desktop platforms, ensuring maximum reach and user satisfaction."
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 40 }} />,
    title: "Performance Optimization",
    description: "We prioritize speed and efficiency in every project, creating lightning-fast applications that provide exceptional user experiences."
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    title: "Secure Development",
    description: "Security is built into our development process, ensuring your applications are protected against vulnerabilities and comply with industry standards."
  },
  {
    icon: <IntegrationInstructionsIcon sx={{ fontSize: 40 }} />,
    title: "Seamless Integration",
    description: "We specialize in integrating complex systems and APIs, creating unified solutions that work harmoniously with your existing infrastructure."
  },
  {
    icon: <SupportAgentIcon sx={{ fontSize: 40 }} />,
    title: "Dedicated Support",
    description: "Our commitment extends beyond deployment with comprehensive maintenance, updates, and technical support to keep your software running optimally."
  }
];

const WhyChooseSection = () => {
  const sectionRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const isMobile = useMediaQuery('(max-width:960px)');
  const [currentFeature, setCurrentFeature] = useState(0);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"]
  });

  useEffect(() => {
    let scrollInterval;
    
    if (isMobile && autoScrollEnabled) {
      scrollInterval = setInterval(() => {
        if (scrollContainerRef.current) {
          const nextFeature = (currentFeature + 1) % features.length;
          const cardWidth = scrollContainerRef.current.offsetWidth;
          
          // If we're at the last feature, smoothly scroll to the first one
          if (currentFeature === features.length - 1) {
            scrollContainerRef.current.scrollTo({
              left: 0,
              behavior: 'smooth'
            });
          } else {
            scrollContainerRef.current.scrollTo({
              left: cardWidth * nextFeature,
              behavior: 'smooth'
            });
          }
          
          setCurrentFeature(nextFeature);
        }
      }, 5000); // Change card every 3 seconds
    }

    return () => {
      if (scrollInterval) {
        clearInterval(scrollInterval);
      }
    };
  }, [currentFeature, isMobile, autoScrollEnabled]);

  const handleScroll = () => {
    if (scrollContainerRef.current && isMobile) {
      const scrollPosition = scrollContainerRef.current.scrollLeft;
      const cardWidth = scrollContainerRef.current.offsetWidth;
      const newFeature = Math.round(scrollPosition / cardWidth);
      
      if (newFeature !== currentFeature) {
        setCurrentFeature(newFeature);
      }
    }
  };

  const handleTouchStart = () => {
    setAutoScrollEnabled(false);
  };

  const handleTouchEnd = () => {
    // Re-enable auto-scroll after 5 seconds of no interaction
    setTimeout(() => setAutoScrollEnabled(true), 5000);
  };

  return (
    <div className="why-choose-section" ref={sectionRef}>
      <div className="why-choose-content">
        <motion.div 
          className="why-choose-left"
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <Typography variant="h2" className="why-choose-title">
            Why Choose Virves For
            <span className="highlight"> Software Development</span> Services?
          </Typography>
          <Typography variant="body1" className="why-choose-subtitle">
            We specialize in creating robust, scalable, and innovative software solutions that drive business growth. Our expert team combines technical excellence with industry best practices to deliver results that exceed expectations.
          </Typography>
          {isMobile && (
            <div className="feature-indicators">
              {features.map((_, index) => (
                <div
                  key={index}
                  className={`feature-indicator ${index === currentFeature ? 'active' : ''}`}
                  onClick={() => {
                    if (scrollContainerRef.current) {
                      scrollContainerRef.current.scrollTo({
                        left: scrollContainerRef.current.offsetWidth * index,
                        behavior: 'smooth'
                      });
                      setCurrentFeature(index);
                    }
                  }}
                />
              ))}
            </div>
          )}
        </motion.div>

        {isMobile ? (
          <div 
            className="why-choose-right mobile"
            ref={scrollContainerRef}
            onScroll={handleScroll}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="features-container">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="feature-card"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ 
                    duration: 0.8,
                    delay: index * 0.1,
                    ease: 'easeOut'
                  }}
                >
                  <div className="feature-icon">
                    {feature.icon}
                  </div>
                  <Typography variant="h5" className="feature-title">
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" className="feature-description">
                    {feature.description}
                  </Typography>
                </motion.div>
              ))}
            </div>
          </div>
        ) : (
          <div className="why-choose-right">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 100 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ 
                  duration: 0.8,
                  delay: index * 0.1,
                  ease: 'easeOut'
                }}
              >
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <Typography variant="h5" className="feature-title">
                  {feature.title}
                </Typography>
                <Typography variant="body1" className="feature-description">
                  {feature.description}
                </Typography>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhyChooseSection; 